import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Loader2, Mic, Square, Trash2 } from "lucide-react";

import { useToast } from "@/hooks/use-toast";
import { AppDialog } from "@/components/ui/AppDialog";
import { DictationPlayer } from "@/components/tests/DictationPlayer";
import { RecordingWaveform } from "@/components/tests/RecordingWaveform";
import {
  deleteOwnRecording,
  fetchOwnRecording,
  uploadRecording,
} from "@/api/tests-api";

interface TestRecorderProps {
  attemptId: string;
  assignmentCaseId: string;
  /** Already has a take. Recording again replaces it. */
  hasRecording: boolean;
  /** Length of that take, for the player's label before it loads. */
  recordingSeconds?: number | null;
  onRecorded: () => void;
  /** The take was thrown away; the case is unanswered again. */
  onDeleted?: () => void;
  disabled?: boolean;
  /** "bar" is one row under the images (round 1's reading screen);
   *  "panel" is the stacked card, used in the full-screen dock. The same
   *  instance switches between them, so a take in progress survives. */
  variant?: "panel" | "bar";
  /** Drawn at the end of the bar -- the host's hint and its toggle. */
  trailing?: ReactNode;
}

type State = "idle" | "recording" | "uploading";

/**
 * Say what actually went wrong.
 *
 * This used to swallow the error and print "Could not upload that
 * recording — press Retry", which is the WORST possible message when
 * retrying cannot work: a candidate under a clock presses it again and
 * again while the real cause (transcription not configured on the
 * server) goes unmentioned to the one person who could report it.
 *
 * So the status decides. Some of these a candidate can act on; some
 * they can only report, and for those the message says so rather than
 * inviting a retry that will fail identically.
 */
function describeUploadFailure(err: unknown): {
  title: string;
  description: string;
} {
  const res = (err as { response?: { status?: number; data?: { detail?: string } } })
    ?.response;
  const status = res?.status;
  const detail = res?.data?.detail;

  switch (status) {
    case 503:
      // The server cannot transcribe at all. Nothing was stored, and
      // pressing Retry will fail the same way every time.
      return {
        title: "The server cannot accept recordings right now",
        description:
          (detail ? `${detail}. ` : "") +
          "This is a problem with the system, not with your recording. " +
          "Tell whoever is running the assessment — your other answers " +
          "are unaffected.",
      };
    case 409:
      return {
        title: "This assessment is already finished",
        description:
          "It cannot take any more recordings. Nothing you recorded " +
          "earlier has been lost.",
      };
    case 413:
      return {
        title: "That recording is too long",
        description:
          detail ??
          "Record your report in shorter passages and send them one at a time.",
      };
    case 422:
      return {
        title: "That recording could not be read",
        description:
          "The audio did not come through. Record it again — the old take " +
          "is still here until you do.",
      };
    case 429:
    case 502:
      return {
        title: "Transcription is busy",
        description:
          "Your take has been kept — press Retry in a moment. If it keeps " +
          "failing, tell whoever is running the assessment.",
      };
    default:
      return {
        title: "Could not upload that recording",
        description: detail
          ? `${detail}. It has been kept — press Retry to send it again.`
          : "It has been kept — press Retry to send it again.",
      };
  }
}

/**
 * Record the report for one case.
 *
 * The recording IS the answer — there is no text box, and the candidate
 * never sees a transcript. Transcription happens afterwards, for the
 * reviewer's benefit.
 *
 * Two things this is careful about:
 *
 * **Nothing is discarded silently.** If the upload fails the take is kept
 * in memory and a retry button appears. Losing a candidate's answer to a
 * flaky network, under a running clock, would be indefensible.
 *
 * **The microphone is released.** `getUserMedia` keeps the OS recording
 * indicator lit until every track is stopped, which is alarming on a
 * shared clinic machine.
 */
export function TestRecorder({
  attemptId,
  assignmentCaseId,
  hasRecording,
  recordingSeconds,
  onRecorded,
  onDeleted,
  disabled,
  variant = "panel",
  trailing,
}: TestRecorderProps) {
  const { toast } = useToast();
  const [state, setState] = useState<State>("idle");
  const [elapsed, setElapsed] = useState(0);
  const [pending, setPending] = useState<Blob | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Bumped on every successful upload and on delete, and used as the
  // player's key. DictationPlayer caches the object URL it fetched, on
  // purpose -- a reviewer scrubbing back and forth must not re-download.
  // Here the audio behind the same case CHANGES, so without a remount a
  // candidate who re-records would press play and hear the take they
  // just replaced, which is indistinguishable from the new recording
  // having failed to save.
  const [take, setTake] = useState(0);
  // The live capture stream, mirrored into state so the waveform can
  // render from it. `streamRef` is not enough: a ref does not re-render,
  // and the trace has to start when recording does.
  const [liveStream, setLiveStream] = useState<MediaStream | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);

  const releaseMic = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setLiveStream(null);
  }, []);

  // Release the microphone if the component goes away mid-recording —
  // navigating between cases must not leave the mic live.
  useEffect(() => releaseMic, [releaseMic]);

  useEffect(() => {
    if (state !== "recording") {
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
      return;
    }
    timerRef.current = window.setInterval(
      () => setElapsed((e) => e + 1),
      1000,
    );
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [state]);

  const upload = useCallback(
    async (blob: Blob) => {
      setState("uploading");
      try {
        const res = await uploadRecording(attemptId, assignmentCaseId, blob);
        setPending(null);
        setState("idle");
        setTake((n) => n + 1);
        onRecorded();
        if (res.late) {
          toast({
            title: "Recorded after the deadline",
            description:
              "Your recording was saved and marked as after time. It has not been discarded.",
          });
        }
      } catch (err) {
        // Keep the take. The candidate can retry without re-recording.
        setPending(blob);
        setState("idle");
        const { title, description } = describeUploadFailure(err);
        toast({ title, description, variant: "error", duration: 10_000 });
      }
    },
    [attemptId, assignmentCaseId, onRecorded, toast],
  );

  const start = async () => {
    if (disabled || state !== "idle") return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setLiveStream(stream);
      chunksRef.current = [];
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        releaseMic();
        const blob = new Blob(chunksRef.current, { type: rec.mimeType });
        chunksRef.current = [];
        if (blob.size > 0) void upload(blob);
        else setState("idle");
      };
      recorderRef.current = rec;
      setElapsed(0);
      setPending(null);
      rec.start();
      setState("recording");
    } catch {
      toast({
        title: "Microphone unavailable",
        description:
          "Allow microphone access in your browser, then try again.",
        variant: "error",
      });
    }
  };

  const stop = () => {
    if (state !== "recording") return;
    recorderRef.current?.stop();
    recorderRef.current = null;
  };

  const doDelete = async () => {
    setConfirmDelete(false);
    setDeleting(true);
    try {
      await deleteOwnRecording(attemptId, assignmentCaseId);
      setTake((n) => n + 1);
      onDeleted?.();
      toast({
        title: "Recording deleted",
        description: "This case is unanswered again. Record it when you are ready.",
      });
    } catch (err) {
      // Say what the server said. A delete that silently does nothing
      // leaves the candidate believing a take they disown has been
      // removed -- the worst possible failure for this particular
      // button.
      const detail = (
        err as { response?: { data?: { detail?: string } } }
      )?.response?.data?.detail;
      toast({
        title: "Could not delete that recording",
        description: detail
          ? `${detail}.`
          : "It is still saved. Try again, or record over it instead.",
        variant: "error",
      });
    } finally {
      setDeleting(false);
    }
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(1, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  const deleteDialog = (
    <AppDialog
      open={confirmDelete}
      cy="dialog-delete-recording"
      tone="danger"
      title="Delete this recording?"
      body="This case goes back to having no answer. You can record it again afterwards — but this take cannot be brought back."
      onDismiss={() => setConfirmDelete(false)}
      actions={[
        {
          label: "Delete it",
          cy: "btn-confirm-delete-recording",
          variant: "danger",
          onClick: () => void doDelete(),
        },
        {
          label: "Keep it",
          cy: "btn-cancel-delete-recording",
          variant: "ghost",
          autoFocus: true,
          onClick: () => setConfirmDelete(false),
        },
      ]}
    />
  );

  // ONE ROW: the button, the time, then the widest thing there is to show
  // -- the live trace while recording, the take's own player once there
  // is one, and a dashed line holding the place before either. The same
  // controls and the same hooks as the panel; only the arrangement moves.
  if (variant === "bar") {
    return (
      <div data-cy="test-recorder" className="sk-rbar">
        {state === "recording" ? (
          <button
            type="button"
            data-cy="btn-stop-recording"
            onClick={stop}
            className="sk-rbar-btn sk-rbar-btn-stop"
          >
            <Square className="h-3.5 w-3.5" aria-hidden />
            Stop
          </button>
        ) : (
          <button
            type="button"
            data-cy="btn-start-recording"
            disabled={disabled || state === "uploading"}
            onClick={() => {
              void start();
            }}
            className="sk-rbar-btn"
          >
            {state === "uploading" ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <span aria-hidden className="sk-rbar-dot" />
            )}
            {state === "uploading"
              ? "Sending…"
              : hasRecording
                ? "Record again"
                : "Record"}
          </button>
        )}

        <span
          {...(state === "recording" ? { "data-cy": "recording-elapsed" } : {})}
          className={`sk-rbar-time${state === "recording" ? " sk-rbar-time-on" : ""}`}
        >
          {state === "recording" ? `${mm}:${ss}` : "0:00"}
        </span>

        <div className="sk-rbar-wave">
          {state === "recording" ? (
            <>
              <RecordingWaveform stream={liveStream} active />
              <span className="sr-only" role="status">
                Recording in progress.
              </span>
            </>
          ) : hasRecording && !pending ? (
            <DictationPlayer
              key={`${assignmentCaseId}-${take}`}
              load={() => fetchOwnRecording(attemptId, assignmentCaseId)}
              durationHint={recordingSeconds ?? null}
            />
          ) : (
            <div aria-hidden className="sk-rbar-placeholder" />
          )}
        </div>

        {pending && state === "idle" && (
          <button
            type="button"
            data-cy="btn-retry-upload"
            onClick={() => {
              void upload(pending);
            }}
            className="rounded-md border border-status-urgent px-3 py-1.5 text-xs font-semibold text-status-urgent"
          >
            Retry sending
          </button>
        )}

        {hasRecording && state !== "recording" && !pending && (
          <button
            type="button"
            data-cy="btn-delete-recording"
            disabled={disabled || deleting || state === "uploading"}
            onClick={() => setConfirmDelete(true)}
            title="Delete this recording"
            aria-label="Delete this recording"
            className="sk-rbar-icon hover:border-status-urgent hover:text-status-urgent"
          >
            {deleting ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Trash2 className="h-4 w-4" aria-hidden />
            )}
          </button>
        )}

        {trailing}
        {deleteDialog}
      </div>
    );
  }

  return (
    <div
      data-cy="test-recorder"
      className="flex flex-wrap items-center gap-3"
    >
      {state === "recording" ? (
        <button
          type="button"
          data-cy="btn-stop-recording"
          onClick={stop}
          className="inline-flex items-center gap-2 rounded-md bg-status-urgent px-4 py-2 text-sm font-semibold text-white"
        >
          <Square className="h-4 w-4" aria-hidden />
          Stop
        </button>
      ) : (
        <button
          type="button"
          data-cy="btn-start-recording"
          disabled={disabled || state === "uploading"}
          onClick={() => {
            void start();
          }}
          className="sk-sit-record flex w-full items-center justify-center gap-2.5 rounded-[10px] bg-accent px-3.5 py-3.5 text-accent-text hover:bg-accent-hover disabled:opacity-50"
        >
          {state === "uploading" ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Mic className="h-4 w-4" aria-hidden />
          )}
          {state === "uploading"
            ? "Sending…"
            : hasRecording
              ? "Record again"
              : "Record report"}
        </button>
      )}

      {state === "recording" && (
        <span
          data-cy="recording-elapsed"
          className="font-mono text-sm tabular-nums text-status-urgent"
        >
          ● {mm}:{ss}
        </span>
      )}

      {/* The trace, drawn from the SAME stream being muxed -- so it
          cannot show activity the recording does not contain. A counter
          alone ticks upwards identically whether the microphone is
          working, muted, or the wrong device. */}
      {state === "recording" && (
        <div className="w-full">
          <RecordingWaveform stream={liveStream} active />
          {/* Said in text as well as drawn: a moving picture is not an
              accessible signal, and a candidate on a screen reader needs
              the same reassurance. */}
          <span className="sr-only" role="status">
            Recording in progress.
          </span>
        </div>
      )}

      {pending && state === "idle" && (
        <button
          type="button"
          data-cy="btn-retry-upload"
          onClick={() => {
            void upload(pending);
          }}
          className="rounded-md border border-status-urgent px-3 py-1.5 text-xs font-semibold text-status-urgent"
        >
          Retry sending
        </button>
      )}

      {/* SAID ONCE. The page states what a report is -- Findings, then
          Impression -- right above this, and a second sentence here
          said it again in different words. What is left is the thing
          only the recorder knows: that a new take replaces the old. */}
      {hasRecording && state === "idle" && !pending && (
        <p className="text-xs text-foreground-muted">
          Recorded. Recording again replaces it.
        </p>
      )}

      {/* Hear it back, and throw it away.
          Both are the candidate's own take on their own open attempt.
          The player is the same component the reviewer uses -- one
          playback implementation, so speed control and the duration
          behaviour cannot drift between the two screens. */}
      {hasRecording && state !== "recording" && !pending && (
        <div className="w-full border-t border-border pt-3">
          <DictationPlayer
            key={`${assignmentCaseId}-${take}`}
            load={() => fetchOwnRecording(attemptId, assignmentCaseId)}
            durationHint={recordingSeconds ?? null}
          />
          <button
            type="button"
            data-cy="btn-delete-recording"
            disabled={disabled || deleting || state === "uploading"}
            onClick={() => setConfirmDelete(true)}
            className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground-muted transition-colors hover:border-status-urgent hover:text-status-urgent disabled:opacity-50"
          >
            {deleting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            )}
            {deleting ? "Deleting…" : "Delete this recording"}
          </button>
        </div>
      )}

      {deleteDialog}
    </div>
  );
}
